import { computed, defineComponent, h } from 'vue'
import { QBtn, QIcon } from 'quasar'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { quasarHandlers } from '../quasarHandlers'

/**
 * One share button. Which network it is comes from a flag rather than a name, and Facebook is the
 * fall-through — a button with no flag at all shares to Facebook.
 *
 * Rendered as an anchor (`type: 'a'`) so the share URL is a real link rather than a scripted navigation.
 */
export default defineComponent({
  name: 'BsShareBtn',

  props: {
    ...bsComponentProps,
    url: { type: String, default: void 0 },
    /** Pinterest needs the image and description appended, which the caller builds. */
    pinterestQuery: { type: String, default: '' },
    facebook: { type: Boolean, default: false },
    twitter: { type: Boolean, default: false },
    pinterest: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    const bs = useBsComponent({ vm: useVm(), props, emit })

    const btnData = computed((): { icon: string, shareLink: string, color: string } => {
      if (props.pinterest) {
        return {
          icon: 'fab fa-pinterest',
          shareLink: `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(props.url)}${props.pinterestQuery}`,
          color: 'bs-pinterest-r'
        }
      } else if (props.twitter) {
        return {
          icon: 'app:x',
          shareLink: `https://x.com/intent/post?url=${encodeURIComponent(props.url)}`,
          color: 'bs-x-b'
        }
      } else {
        return {
          icon: 'fab fa-facebook',
          shareLink: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(props.url)}&amp;src=sdkpreparse`,
          color: 'bs-fb-b'
        }
      }
    })

    return { ...bs, btnData }
  },

  methods: {
    /** Renamed from `__renderIcon`: Vue does not proxy keys beginning with `_`. */
    renderIcon () {
      return h(QIcon, {
        name: this.btnData.icon,
        size: '1.375rem'
      })
    }
  },

  render () {
    return h(QBtn, {
      class: 'bs-share-btn',
      ...this.getRenderProps({
        type: 'a',
        color: this.btnData.color,
        unelevated: true
      }),
      ...this.getAttributes({
        href: this.btnData.shareLink,
        target: '_blank'
      }),
      ...quasarHandlers(this.getListeners())
    // QBtn is a component: children have to be a slot function.
    }, { default: () => [
      this.renderIcon(),
      this.slot(this, 'default')
    ] })
  }
})
